import { Pipe, PipeTransform } from '@angular/core';
import { CurrencyContextService } from '../services/general/currency-context.service';

/**
 * A money figure that KNOWS WHICH BOOK IT IS IN (multi-currency P2-6).
 *
 * ── The problem it exists to solve ───────────────────────────────────────────────────────────────────────────
 * 38 money figures across nine screens were written as `$ {{ x | number:'1.2-2' }}` — a literal dollar sign in
 * the markup, printed whatever book the figure is actually in. Today every figure IS dollars, so it reads
 * correctly. The moment a client is billed in ZiG, the same markup prints a ZiG amount wearing a dollar sign:
 * not a formatting slip, a different amount of money. There is no exchange rate in this system, by design (a
 * ZIMRA liability), so a mislabelled figure cannot be reconciled back to the truth by anyone downstream.
 *
 * ── The rule, which is the API's own ─────────────────────────────────────────────────────────────────────────
 * Copied deliberately from `FinanceDashboardController::payableMoneyLabel`, so a figure reads the same whether
 * the server or the browser rendered it:
 *
 *   - **the BASE keeps the literal `$`** it has always printed, so nothing an operator sees changes today;
 *   - **any other book is NAMED** — `ZIG 1,234.56` — because that is the only honest way to print it.
 *
 * That is why this pipe exists rather than reusing {@see DefaultCurrencyPipe}, which suffixes the code on every
 * figure (`1,234.56 USD`). Both are correct; this one keeps the promise the rest of the programme keeps, that
 * **nothing visible changes while settlement is off**.
 *
 * ── And UNKNOWN is not ZERO ─────────────────────────────────────────────────────────────────────────────────
 * Most of those 38 sites were written `$ {{ (r.amount || 0) | number:'1.2-2' }}`. That `|| 0` turns "we could
 * not work this out" into a confident `$ 0.00` — the single worst thing a money screen can say, because
 * "nothing owed" and "we do not know" lead to opposite actions. It is reachable today on a half-loaded screen,
 * and reachable BY DESIGN once ZiG is on: decision D2 has the API return `null` for a line it cannot price in
 * the book it is settled in, precisely so the gap is visible. This pipe prints the same marker the API prints
 * on a document, and never a number.
 */
@Pipe({
  name: 'money',
  // Impure: the base is read from a context that is fetched once, asynchronously, and a figure rendered
  // before it lands must re-render after. The work is a string format on a number — no allocation worth
  // memoising — and the same trade-off the two existing currency pipes already make.
  pure: false,
})
export class MoneyPipe implements PipeTransform {
  constructor(private currency: CurrencyContextService) {}

  /**
   * @param value    the amount. null / undefined / NaN reads as the unavailable marker, never as 0.
   * @param currency the book this figure is in. Absent means base money — which is every figure in
   *                 production today, and exactly what an undecorated API payload means.
   */
  transform(value: number | null | undefined, currency?: string | null): string {
    // UNKNOWN IS NOT ZERO. Checked first, and deliberately not with `== 0`: `null == 0` is false in
    // JavaScript, which is how a missing figure used to slip through and print as a confident zero.
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return CurrencyContextService.UNAVAILABLE;
    }

    const amount = Number(value);
    const isNegative = amount < 0;
    const formatted = Math.abs(amount)
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, '$&,');

    const code = (currency ?? '').trim().toUpperCase();
    const isBase = code === '' || code === this.currency.base.trim().toUpperCase();

    // Negatives keep the parentheses the statements already use, so a credit still reads as a credit.
    const body = isBase ? '$' + formatted : code + ' ' + formatted;

    return isNegative ? '(' + body + ')' : body;
  }
}
